/**
 * The one dialog every operational command uses.
 *
 * It does three things and decides nothing:
 *  - builds the form from the action's field list;
 *  - sends ONE idempotency key for as long as the dialog is open, so pressing the button twice on a bad
 *    connection records one collection, one receipt, one pour — not two;
 *  - shows the database's refusal in the database's words. `RTC_UNDECIDED_POLICY` (nobody has said who
 *    captures here, or which lots a partial draw takes) is a question for the client and is rendered as one;
 *    `RTC_SOURCE_OWNED` means the field application captures this, and says so.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rpc, supabase } from '../../lib/supabase';
import { Dialog } from '../../ui/overlays';
import { Checkbox, Field, Form, Input, Select, Textarea } from '../../ui/forms';
import type { OpsAction, OpsField, OpsRow, OpsValues } from './types';

function RefSelect({ field, value, onChange }: { field: Extract<OpsField, { kind: 'ref' }>; value: string; onChange: (v: string) => void }) {
  const options = useQuery({
    queryKey: ['ops-ref', field.view, field.where ?? null, field.labelColumns],
    staleTime: 60_000,
    queryFn: async () => {
      const valueColumn = field.valueColumn ?? 'id';
      let q = supabase.from(field.view).select([...new Set([valueColumn, ...field.labelColumns])].join(',')).order(field.labelColumns[0]!).limit(1000);
      if (!field.noStatus) q = q.eq('status', 'ACTIVE');
      for (const [column, expected] of Object.entries(field.where ?? {})) {
        q = expected.startsWith('in.') ? q.filter(column, 'in', expected.slice(3)) : q.eq(column, expected);
      }
      const { data, error } = await q;
      if (error) throw error;
      return ((data ?? []) as unknown as Record<string, unknown>[]).map((r) => ({
        value: String(r[valueColumn]),
        label: field.labelColumns.map((c) => r[c]).filter(Boolean).join(' · '),
      }));
    },
  });
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} disabled={options.isLoading}
            placeholder={options.isLoading ? 'Loading…' : field.required ? 'Choose…' : 'None'} options={options.data ?? []} />
  );
}

export function ActionDialog({ action, row, registerId, onClose, onDone }: {
  action: OpsAction | null;
  row: OpsRow | null;
  registerId: string;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<OpsValues>({});
  // One key per opening. A retry of the same submission is the same fact; reopening the dialog is a new one.
  const idempotencyKey = useMemo(() => crypto.randomUUID(), [action, row]);

  useEffect(() => {
    if (!action) return;
    const initial: OpsValues = {};
    for (const field of action.fields) initial[field.key] = field.kind === 'boolean' ? false : '';
    setValues({ ...initial, ...(action.initial?.(row) ?? {}) });
  }, [action, row]);

  const run = useMutation({
    mutationFn: async () => rpc<Record<string, unknown>>(action!.rpc, action!.params(action!.prepare ? await action!.prepare(values) : values, row, idempotencyKey)),
    onSuccess: async (result) => {
      for (const id of [registerId, ...(action!.invalidates ?? [])]) await queryClient.invalidateQueries({ queryKey: ['ops', id] });
      await queryClient.invalidateQueries({ queryKey: ['entity-timeline'] });
      const warnings = Array.isArray(result.warnings) ? (result.warnings as string[]) : [];
      onDone([action!.success(result), ...warnings.map((w) => `Note: ${w}.`)].join(' '));
      onClose();
    },
  });

  useEffect(() => { run.reset(); }, [action, row]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!action) return null;
  const missing = action.fields.some((f) => 'required' in f && f.required && String(values[f.key] ?? '').trim() === '');

  return (
    <Dialog open onClose={onClose} title={action.label} width="40rem">
      {action.intro ? <p className="mb-4 text-small text-ink-muted">{action.intro}</p> : null}
      <Form onSubmit={() => run.mutateAsync().then(() => undefined).catch(() => undefined)} submitLabel={action.submitLabel ?? action.label}
            onCancel={onClose} error={run.error as { code?: string; message?: string } | null} disabled={missing} columns={2}>
        {action.fields.map((field) => {
          const value = values[field.key] ?? '';
          const set = (next: string | boolean) => setValues((current) => ({ ...current, [field.key]: next }));
          if (field.kind === 'boolean') {
            return <div key={field.key} className="pt-5"><Checkbox checked={Boolean(value)} onChange={(e) => set(e.target.checked)} label={field.label} description={field.hint} /></div>;
          }
          const wide = field.kind === 'lines' || field.kind === 'textarea';
          return (
            <Field key={field.key} label={field.label} required={'required' in field ? field.required : false} hint={field.hint} className={wide ? 'col-span-2' : undefined}>
              {field.kind === 'select' ? (
                <Select value={String(value)} onChange={(e) => set(e.target.value)} placeholder={field.required ? 'Choose…' : 'Not set'} options={field.options} />
              ) : field.kind === 'ref' ? (
                <RefSelect field={field} value={String(value)} onChange={set} />
              ) : wide ? (
                <Textarea value={String(value)} onChange={(e) => set(e.target.value)} rows={field.kind === 'lines' ? 4 : 2} className={field.kind === 'lines' ? 'font-mono' : undefined} />
              ) : (
                <Input value={String(value)} onChange={(e) => set(e.target.value)} type={field.kind === 'date' ? 'date' : 'text'}
                       inputMode={field.kind === 'grams' || field.kind === 'integer' ? 'numeric' : field.kind === 'celsius' ? 'decimal' : undefined}
                       className={field.kind === 'scan' ? 'font-mono uppercase' : undefined}
                       placeholder={field.kind === 'grams' ? 'grams — 50 kg is 50000' : field.kind === 'celsius' ? '°C, e.g. 3.5' : field.kind === 'scan' ? 'Scan or type the code on the label' : undefined} />
              )}
            </Field>
          );
        })}
      </Form>
    </Dialog>
  );
}

/* ── turning what was typed into what the command takes ───────────────────────────────────────────── */
const text = (v: string | boolean | undefined) => { const s = String(v ?? '').trim(); return s === '' ? null : s; };
export const asText = text;
export const asScan = (v: string | boolean | undefined) => text(v)?.toUpperCase() ?? null;
/** An empty box is "not given" — null — never zero. */
export const asInteger = (v: string | boolean | undefined) => { const s = text(v); return s === null ? null : Math.round(Number(s)); };
/** Degrees as typed → tenths of a degree, which is what the database stores. */
export const asC10 = (v: string | boolean | undefined) => { const s = text(v); return s === null ? null : Math.round(Number(s) * 10); };
/**
 * A textarea of ids, one per line, as the uuid[] a bulk command takes. Blank lines are dropped; nothing else
 * is. An unparseable line is passed through for the database to refuse by name, because a client that
 * quietly discards a row somebody pasted is how a bulk action silently does less than it was asked to.
 */
export const asIdList = (v: string | boolean | undefined) => {
  const s = text(v);
  if (s === null) return null;
  const ids = s.split(/[\s,]+/).map((part) => part.trim()).filter(Boolean);
  return ids.length === 0 ? null : ids;
};
/**
 * "Lines" of products, one per line: a product code (G++, GPW++) or a variant id, a comma, then units ("2") or a cycle
 * ("1-0"), given back with each code turned into its id. A code is looked up; anything that is neither a known code nor an id is refused here, by its line, before
 * the database is asked (2026-11-01: a sentence typed into Lines reached set_subscription_lines as a variant id).
 */
export async function asVariantLines(v: string | boolean | undefined): Promise<string> {
  const rows = String(v ?? '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => {
    const at = l.lastIndexOf(',');
    return { product: (at < 0 ? l : l.slice(0, at)).trim(), units: at < 0 ? '' : l.slice(at + 1).trim() };
  });
  const isId = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
  const codes = [...new Set(rows.filter((r) => !isId(r.product)).map((r) => r.product.toUpperCase()))];
  const byCode = new Map<string, string>();
  if (codes.length) {
    const { data, error } = await supabase.from('product_variants').select('id,code').in('code', codes);
    if (error) throw error;
    for (const x of (data ?? []) as { id: string; code: string }[]) byCode.set(x.code.toUpperCase(), x.id);
  }
  return rows.map((r, i) => {
    const id = isId(r.product) ? r.product : byCode.get(r.product.toUpperCase());
    if (!id) throw new Error(`Line ${i + 1}: “${r.product.slice(0, 40)}” is not a product. Write a code such as G++ or GPW++, a comma, then the units, e.g. G++,2`);
    if (!/^\d+(-\d+)*$/.test(r.units)) throw new Error(`Line ${i + 1}: after the comma write the units a day, e.g. G++,2 (or a cycle such as G++,1-0)`);
    return `${id},${r.units}`;
  }).join('\n');
}
